import { setRefreshTokenCallback } from '@/api/middleware/tokenRefresh';
import {
  authenticateUser,
  registerUser,
  revokeToken,
} from '@/features/auth/authService';
import { clearProfile, getProfile } from '@/features/profile/profileActions';
import { useDispatch } from '@/store/utils';
import type { LoginResponse } from '@/types/auth';
import type { CreateCustomerProfileRequestBodyDTO } from '@/types/dto/agent';
import {
  clearTokens,
  getTokenExpiry,
  getTokensRef,
  setAuthData,
} from '@/utils/auth';
import type { LoginValues } from '@/utils/zodSchema';
import {
  createContext,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

export interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  logout: () => Promise<void>;
  loginWithPassword: (values: LoginValues) => Promise<LoginResponse>;
  registerWithPassword: (
    values: CreateCustomerProfileRequestBodyDTO
  ) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [tokenUpdateTrigger, setTokenUpdateTrigger] = useState(0);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleLoginSuccess = useCallback(
    async (response: LoginResponse) => {
      setAuthData(response.tokens_ref, response.expires_in);
      await dispatch(getProfile()).unwrap();
      setTokenUpdateTrigger((prev) => prev + 1);
    },
    [dispatch]
  );

  const logout = useCallback(async () => {
    try {
      const tokensRef = getTokensRef();

      if (tokensRef) {
        await revokeToken({
          tokens_ref: tokensRef,
        });
      }
    } finally {
      clearTokens();
      dispatch(clearProfile());
      navigate('/login');
    }
  }, [navigate, dispatch]);

  const refreshAccessToken = useCallback(
    async (tokensRef: string) => {
      if (!tokensRef) {
        throw new Error('No tokens ref available');
      }

      try {
        const response = await authenticateUser({
          grant_type: 'refresh_token',
          tokens_ref: tokensRef,
        });

        if (!response.tokens_ref || !response.expires_in) {
          throw new Error('Invalid token response');
        }

        setAuthData(response.tokens_ref, response.expires_in);
        setTokenUpdateTrigger((prev) => prev + 1);
      } catch (error) {
        await logout();
        throw error;
      }
    },
    [logout]
  );

  const loginWithPassword = useCallback(
    async (values: LoginValues): Promise<LoginResponse> => {
      const response = await authenticateUser({
        grant_type: 'password',
        username: values.email,
        password: values.password,
      });

      await handleLoginSuccess(response);
      return response;
    },
    [handleLoginSuccess]
  );

  const registerWithPassword = useCallback(
    async (values: CreateCustomerProfileRequestBodyDTO): Promise<void> => {
      await registerUser({
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumber: values.phoneNumber,
        password: values.password,
        consents: values.consents,
        isMemberOfLoyaltyProgram: false,
      });
    },
    []
  );

  useEffect(() => {
    setIsLoading(false);
  }, []);

  const tokensRef = getTokensRef();
  const currentTokenExpiry = getTokenExpiry();

  useEffect(() => {
    if (!tokensRef || !currentTokenExpiry) return;

    const timeUntilExpiry = Number(currentTokenExpiry) - Date.now();
    // Refresh at 95% of token lifetime to minimize window where token could expire
    // between scheduled refreshes while still providing buffer for network/processing delays
    const refreshDelay = timeUntilExpiry * 0.95;

    const refreshTimeout = setTimeout(async () => {
      await refreshAccessToken(tokensRef);
    }, refreshDelay);

    return () => clearTimeout(refreshTimeout);
  }, [refreshAccessToken, tokensRef, currentTokenExpiry, tokenUpdateTrigger]);

  // Register refresh callback for 401 error interceptor
  useEffect(() => {
    const tokensRef = getTokensRef();

    if (tokensRef) {
      // Register callback so interceptor can trigger refresh on 401 errors
      setRefreshTokenCallback(() => refreshAccessToken(tokensRef));
    }

    return () => {
      // Clean up on unmount
      setRefreshTokenCallback(() => Promise.resolve());
    };
  }, [refreshAccessToken]);

  const value = {
    isAuthenticated: !!getTokensRef(),
    isLoading,
    logout,
    loginWithPassword,
    registerWithPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
